import type { Vendor } from '@shopgnd/shared';
import { resolveVendorPlanId } from '@shopgnd/shared';
import type { PlanId } from '@/lib/vendorPlans';
import { getPlanDefinition } from '@shopgnd/shared';
import { getRuntimePlanDefinitions } from '@/lib/platformPlanConfig';

export type SubscriptionLaunchSettings = {
  subscriptionsFreeLaunchEnabled: boolean;
  subscriptionsFreeUntil?: string;
  subscriptionsFreeMessage?: string;
};

/** Default launch promo — free paid plans until this date (admin can change). */
export const DEFAULT_SUBSCRIPTION_FREE_UNTIL = '2026-08-30T23:59:59.000Z';

export const DEFAULT_SUBSCRIPTION_LAUNCH: SubscriptionLaunchSettings = {
  subscriptionsFreeLaunchEnabled: true,
  subscriptionsFreeUntil: DEFAULT_SUBSCRIPTION_FREE_UNTIL,
};

export type VendorSubscriptionFields = Pick<
  Vendor,
  | 'subscriptionPlanId'
  | 'subscriptionTrialEndsAt'
  | 'subscriptionStatus'
  | 'stripeSubscriptionId'
  | 'subscriptionWaived'
  | 'subscriptionWaivedUntil'
  | 'subscriptionCurrentPeriodEnd'
>;

export function planRequiresPaidSubscription(planId: PlanId | undefined): boolean {
  if (!planId) return false;
  const plan = getPlanDefinition(planId, getRuntimePlanDefinitions());
  return plan.monthlyPriceCents > 0;
}

export function isFreeLaunchActive(
  settings: SubscriptionLaunchSettings,
  now = new Date(),
): boolean {
  if (!settings.subscriptionsFreeLaunchEnabled) return false;
  if (!settings.subscriptionsFreeUntil) return true;
  return now.getTime() <= new Date(settings.subscriptionsFreeUntil).getTime();
}

export function freeLaunchEndsAt(settings: SubscriptionLaunchSettings): Date | null {
  if (!settings.subscriptionsFreeUntil) return null;
  const d = new Date(settings.subscriptionsFreeUntil);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatLaunchDate(iso?: string): string {
  if (!iso) return 'the end of the launch period';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'the end of the launch period';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export function freeUntilLabel(settings: SubscriptionLaunchSettings): string {
  return formatLaunchDate(settings.subscriptionsFreeUntil);
}

export function chargeStartsMessage(settings: SubscriptionLaunchSettings): string {
  const custom = settings.subscriptionsFreeMessage?.trim();
  if (custom) return custom;
  const until = freeUntilLabel(settings);
  return `Your card will be charged automatically after ${until}, when the launch free period ends.`;
}

export function requiresCardAtSignup(
  planId: PlanId,
  settings: SubscriptionLaunchSettings,
  now = new Date(),
): boolean {
  if (!planRequiresPaidSubscription(planId)) return false;
  return !isFreeLaunchActive(settings, now);
}

export function trialEndsAtForSignup(settings: SubscriptionLaunchSettings, now = new Date()): string | undefined {
  if (isFreeLaunchActive(settings, now) && settings.subscriptionsFreeUntil) {
    return settings.subscriptionsFreeUntil;
  }
  if (isFreeLaunchActive(settings, now)) {
    return undefined;
  }
  const trial = new Date(now);
  trial.setDate(trial.getDate() + 14);
  return trial.toISOString();
}

export function trialEndUnixForVendor(
  vendor: VendorSubscriptionFields,
  settings: SubscriptionLaunchSettings,
  now = new Date(),
): number | undefined {
  const iso = vendor.subscriptionTrialEndsAt ?? trialEndsAtForSignup(settings, now);
  if (!iso) return undefined;
  const unix = Math.floor(new Date(iso).getTime() / 1000);
  return unix > Math.floor(now.getTime() / 1000) ? unix : undefined;
}

export function isVendorSubscriptionWaived(vendor: VendorSubscriptionFields, now = new Date()): boolean {
  if (vendor.subscriptionWaived === true) return true;
  if (vendor.subscriptionWaivedUntil) {
    return now.getTime() < new Date(vendor.subscriptionWaivedUntil).getTime();
  }
  return false;
}

const ACTIVE_STRIPE_STATUSES = new Set(['active', 'trialing']);

export function hasActiveStripeSubscription(vendor: VendorSubscriptionFields): boolean {
  if (!vendor.stripeSubscriptionId) return false;
  const status = vendor.subscriptionStatus?.toLowerCase();
  if (!status) return true;
  return ACTIVE_STRIPE_STATUSES.has(status);
}

export function vendorInLaunchFreePeriod(
  vendor: VendorSubscriptionFields,
  settings: SubscriptionLaunchSettings,
  now = new Date(),
  vendorLoaded = true,
): boolean {
  const planId = resolvedPlanIdForVendor(vendor, vendorLoaded);
  if (!planId || !planRequiresPaidSubscription(planId)) return false;
  if (hasActiveStripeSubscription(vendor) && vendor.subscriptionStatus === 'trialing') return true;
  if (vendor.subscriptionTrialEndsAt && now.getTime() <= new Date(vendor.subscriptionTrialEndsAt).getTime()) {
    return true;
  }
  return isFreeLaunchActive(settings, now) && !vendor.stripeSubscriptionId;
}

export function vendorSubscriptionPaymentDue(
  vendor: VendorSubscriptionFields,
  settings: SubscriptionLaunchSettings,
  now = new Date(),
  vendorLoaded = true,
): boolean {
  if (!vendorLoaded) return false;
  if (isVendorPlanUnverified(vendor, vendorLoaded)) return false;
  const planId = resolvedPlanIdForVendor(vendor, vendorLoaded);
  if (!planId || !planRequiresPaidSubscription(planId)) return false;
  if (isVendorSubscriptionWaived(vendor, now)) return false;
  if (hasActiveStripeSubscription(vendor)) return false;
  if (vendorInLaunchFreePeriod(vendor, settings, now)) return false;

  const status = vendor.subscriptionStatus?.toLowerCase();
  if (status === 'past_due' || status === 'unpaid' || status === 'incomplete') return true;

  return true;
}

export function isVendorPlanUnverified(
  vendor: VendorSubscriptionFields,
  vendorLoaded = true,
): boolean {
  return resolveVendorPlanId(vendor.subscriptionPlanId, vendorLoaded).state === 'unverified';
}

function resolvedPlanIdForVendor(
  vendor: VendorSubscriptionFields,
  vendorLoaded = true,
): PlanId | null {
  const resolution = resolveVendorPlanId(vendor.subscriptionPlanId, vendorLoaded);
  if (resolution.state !== 'resolved') return null;
  return resolution.planId;
}

export function planRequiresPaidSubscriptionForVendor(
  vendor: VendorSubscriptionFields,
  vendorLoaded = true,
): boolean {
  const planId = resolvedPlanIdForVendor(vendor, vendorLoaded);
  if (!planId) return false;
  return planRequiresPaidSubscription(planId);
}

export function vendorHubBlocked(
  vendor: VendorSubscriptionFields,
  settings: SubscriptionLaunchSettings | null,
  opts?: { vendorLoaded?: boolean; settingsReady?: boolean; now?: Date },
): boolean {
  const vendorLoaded = opts?.vendorLoaded ?? true;
  const settingsReady = opts?.settingsReady ?? true;
  const now = opts?.now ?? new Date();
  if (!settingsReady || !settings) return false;
  if (!vendorLoaded) return false;
  if (isVendorPlanUnverified(vendor, vendorLoaded)) return false;
  return vendorSubscriptionPaymentDue(vendor, settings, now, vendorLoaded);
}

export type SubscriptionAccountStatus =
  | 'free_plan'
  | 'waived'
  | 'launch_free'
  | 'trial'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'payment_due'
  | 'unverified';

export function subscriptionAccountStatus(
  vendor: VendorSubscriptionFields,
  settings: SubscriptionLaunchSettings,
  now = new Date(),
  vendorLoaded = true,
): SubscriptionAccountStatus {
  const planId = resolvedPlanIdForVendor(vendor, vendorLoaded);
  if (isVendorPlanUnverified(vendor, vendorLoaded)) return 'unverified';
  if (!planId || !planRequiresPaidSubscription(planId)) return 'free_plan';
  if (isVendorSubscriptionWaived(vendor, now)) return 'waived';

  const stripeStatus = vendor.subscriptionStatus?.toLowerCase();
  if (stripeStatus === 'active') return 'active';
  if (stripeStatus === 'trialing') return vendorInLaunchFreePeriod(vendor, settings, now) ? 'launch_free' : 'trial';
  if (stripeStatus === 'past_due' || stripeStatus === 'unpaid') return 'past_due';
  if (stripeStatus === 'canceled') return 'canceled';

  if (hasActiveStripeSubscription(vendor)) return 'active';
  if (vendorInLaunchFreePeriod(vendor, settings, now)) {
    return isFreeLaunchActive(settings, now) ? 'launch_free' : 'trial';
  }
  return 'payment_due';
}

export function subscriptionStatusLabel(status: SubscriptionAccountStatus): string {
  switch (status) {
    case 'free_plan':
      return 'Free plan';
    case 'waived':
      return 'Waived';
    case 'launch_free':
      return 'Launch free';
    case 'trial':
      return 'Trial';
    case 'active':
      return 'Active';
    case 'past_due':
      return 'Past due';
    case 'canceled':
      return 'Canceled';
    case 'payment_due':
      return 'Payment required';
    case 'unverified':
      return 'Plan verification needed';
  }
}

export function waiverLabel(vendor: VendorSubscriptionFields): string {
  if (vendor.subscriptionWaived) return 'Forever';
  if (vendor.subscriptionWaivedUntil) return `Until ${formatLaunchDate(vendor.subscriptionWaivedUntil)}`;
  return '—';
}
